import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { ApiResponse } from "models";
import { PaymentState } from "models/payment";

const initialState: PaymentState = {
  loading: false,
  data: {},
  message: "",
  success: false,

  // ✅ ADD PAYMENT STATES
  addLoading: false,
  addMessage: "",
  addSuccess: false,

  // ✅ UPDATE PAYMENT STATES
  updateMessage: "",
  updateLoading: false,

  // ✅ GET BY ID STATES
  paymentByIdLoading: false,
  paymentByIdSuccess: false,
  paymentByIdData: {},

  // ✅ DELETE STATES
  deleteLoading: false,
  deleteMessage: "",

  // ✅ INVOICE STATES
  invoiceNumberData: [],
  invoiceNumberListLoading: false,
  invoiceNumberDataSuccess: false,
  invoiceNumberMessage: "",

  // ✅ PDF STATES
  isPdfLoading: false,
  isPdfSuccess: false,
  pdfData: null,
  pdfMessage: "",
};

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    // ✅ GET PAYMENT LIST
    getPayment(state, _action: PayloadAction<ApiResponse>) {
      state.loading = true;
      state.message = "";
    },
    getPaymentSuccess(state, action: PayloadAction<ApiResponse>) {
      state.loading = false;
      state.data = action.payload;
      state.success = action.payload.success;
    },
    getPaymentFailed(state, action: PayloadAction<ApiResponse>) {
      state.loading = false;
      state.data = {};
      state.message = action.payload.message;
      state.success = action.payload.success;
    },

    // ✅ GET PAYMENT BY ID
    getPaymentById(state, _action: PayloadAction<ApiResponse>) {
      state.paymentByIdLoading = true;
      state.paymentByIdSuccess = false;
    },
    getPaymentByIdSuccess(state, action: PayloadAction<ApiResponse>) {
      state.paymentByIdLoading = false;
      state.paymentByIdData = action.payload.data;
      state.paymentByIdSuccess = action.payload.success;
    },
    getPaymentByIdFailed(state, action: PayloadAction<ApiResponse>) {
      state.paymentByIdLoading = false;
      state.paymentByIdData = {};
      state.paymentByIdSuccess = action.payload.success;
    },

    // ✅ ADD PAYMENT (New)
    addPayment(state, _action: PayloadAction<ApiResponse>) {
      state.addLoading = true;
      state.addMessage = "";
      state.addSuccess = false;
    },
    addPaymentSuccess(state, action: PayloadAction<ApiResponse>) {
      state.addLoading = false;
      state.addMessage = action.payload.message;
      state.addSuccess = action.payload.success;
    },
    addPaymentFailed(state, action: PayloadAction<ApiResponse>) {
      state.addLoading = false;
      state.addMessage = action.payload.message;
      state.addSuccess = action.payload.success;
    },

    // ✅ UPDATE PAYMENT
    updatePayment(state, _action: PayloadAction<ApiResponse>) {
      state.updateLoading = true;
      state.updateMessage = "";
    },
    updatePaymentSuccess(state, action: PayloadAction<ApiResponse>) {
      state.updateLoading = false;
      state.updateMessage = action.payload.message;
      state.success = action.payload.success;
    },
    updatePaymentFailed(state, action: PayloadAction<ApiResponse>) {
      state.updateLoading = false;
      state.updateMessage = action.payload.message;
      state.success = action.payload.success;
    },

    // ✅ DELETE PAYMENT
    deletePayment(state, _action: PayloadAction<ApiResponse>) {
      state.deleteLoading = true;
      state.deleteMessage = "";
    },
    deletePaymentSuccess(state, action: PayloadAction<ApiResponse>) {
      state.deleteLoading = false;
      state.deleteMessage = action.payload.message;
      state.success = action.payload.success;
    },
    deletePaymentFailed(state, action: PayloadAction<ApiResponse>) {
      state.deleteLoading = false;
      state.deleteMessage = action.payload.message;
      state.success = action.payload.success;
    },

    // ✅ INVOICE LIST
    getInvoiceNumberList(state, _action: PayloadAction<ApiResponse>) {
      state.invoiceNumberListLoading = true;
      state.invoiceNumberMessage = "";
    },
    getInvoiceNumberListSuccess(state, action: PayloadAction<ApiResponse>) {
      state.invoiceNumberListLoading = false;
      state.invoiceNumberData = action.payload.data;
      state.invoiceNumberDataSuccess = action.payload.success;
    },
    getInvoiceNumberListFailed(state, action: PayloadAction<ApiResponse>) {
      state.invoiceNumberListLoading = false;
      state.invoiceNumberData = [];
      state.invoiceNumberMessage = action.payload.message;
      state.invoiceNumberDataSuccess = action.payload.success;
    },

    // ✅ PDF RELATED
    getPaymentPdfRequest(state, _action: PayloadAction<{ _id: string }>) {
      state.isPdfLoading = true;
      state.isPdfSuccess = false;
      state.pdfMessage = "";
      state.error = null;
    },
    getPaymentPdfSuccess(state, action: PayloadAction<any>) {
      state.isPdfLoading = false;
      state.isPdfSuccess = true;
      state.pdfData = action.payload;
      state.pdfMessage = action.payload.message || "PDF fetched successfully";
    },
    getPaymentPdfFailed(state, action: PayloadAction<any>) {
      state.isPdfLoading = false;
      state.isPdfSuccess = false;
      state.error = action.payload.message || "Failed to fetch PDF";
    },
    resetPaymentPdf(state) {
      state.isPdfLoading = false;
      state.isPdfSuccess = false;
      state.pdfData = null;
      state.pdfMessage = "";
      state.error = null;
    },

    // ✅ CLEAR DATA
    clearData(state) {
      state.loading = false;
      state.message = "";
      state.success = false;

      state.addLoading = false;
      state.addMessage = "";
      state.addSuccess = false;

      state.updateMessage = "";
      state.updateLoading = false;

      state.deleteLoading = false;
      state.deleteMessage = "";

      state.invoiceNumberMessage = "";
      state.invoiceNumberListLoading = false;
      state.invoiceNumberDataSuccess = false;

      state.paymentByIdLoading = false;
      state.paymentByIdSuccess = false;
      state.paymentByIdData = {};
    },
  },
});

export const paymentAction = paymentSlice.actions;

// ✅ Selectors
export const getPaymentData = (state: RootState) => state.paymentModule.data;
export const getPaymentById = (state: RootState) => state.paymentModule.paymentByIdData;
export const selectIsLoading = (state: RootState) => state.paymentModule.loading;

// ✅ Add Payment Selectors
export const selectAddLoading = (state: RootState) => state.paymentModule.addLoading;
export const selectAddMessage = (state: RootState) => state.paymentModule.addMessage;
export const selectAddSuccess = (state: RootState) => state.paymentModule.addSuccess;

// ✅ Update Payment Selectors
export const updateLoading = (state: RootState) => state.paymentModule.updateLoading;
export const updateMessage = (state: RootState) => state.paymentModule.updateMessage;

// ✅ Delete Payment Selectors
export const deletePaymentLoad = (state: RootState) => state.paymentModule.deleteLoading;
export const deletePaymentMsg = (state: RootState) => state.paymentModule.deleteMessage;

// ✅ Invoice Selectors
export const invoiceListData = (state: RootState) => state.paymentModule.invoiceNumberData;
export const invoiceNumberListLoad = (state: RootState) => state.paymentModule.invoiceNumberListLoading;
export const invoiceNumberListMessage = (state: RootState) => state.paymentModule.invoiceNumberMessage;
export const invoiceNumberSuccess = (state: RootState) => state.paymentModule.invoiceNumberDataSuccess;

// ✅ PDF Selectors
export const selectPdfLoading = (state: RootState) => state.paymentModule.isPdfLoading;
export const selectPdfSuccess = (state: RootState) => state.paymentModule.isPdfSuccess;
export const selectPdfData = (state: RootState) => state.paymentModule.pdfData;
export const selectPdfMessage = (state: RootState) => state.paymentModule.pdfMessage;

const paymentReducer = paymentSlice.reducer;
export default paymentReducer;
